import { useMemo, useState } from "react";
import { rankSearchMatches, type RankableSearchItem } from "../../lib/format/normalize";

export type StatusFilter = "all" | "active" | "inactive";

export interface CatalogFilterOptions<T> {
  /** Fields to search — all entries are folded together for `rankSearchMatches`'s
   * tier-3 (fuzzy name/description) matching, so every field listed here
   * contributes equally to whether a row matches a free-text query. Order
   * carries no semantic meaning to the caller: internally the first entry is
   * threaded through as `label` and the rest as `description`, but that's an
   * implementation detail of how `rankSearchMatches` is called, not a
   * "this one is the name field" distinction — Products/Operations/Units
   * intentionally list their CODE field first (to preserve table column
   * order), which would be a misleading "label" if read as one. Pass a
   * `code` accessor as the SECOND argument (see below) instead of listing it
   * here, to get the code-aware tier-1/tier-2 ranking. */
  searchFields: Array<(item: T) => string | null | undefined>;
  /** Optional business-code accessor — enables `rankSearchMatches`'s
   * exact/prefix code tiers (including zero-padding equivalence), matching
   * every other search box in the app (Daily Entry pickers, Norm creation).
   * Omit for entities with no code concept (Neutral Activity).
   * Note: Memoization is only effective if the caller memoizes `options`
   * (e.g., with `useMemo`); with an inline `options` literal, this recomputes
   * every render. */
  codeOf?: (item: T) => string | null | undefined;
  /** Omit entirely for entities with no active/status concept (Unit). */
  statusOf?: (item: T) => boolean;
}

export interface CatalogFilterState<T> {
  query: string;
  setQuery: (q: string) => void;
  statusFilter: StatusFilter;
  setStatusFilter: (s: StatusFilter) => void;
  isFiltered: boolean;
  clear: () => void;
  filtered: T[];
}

export function useCatalogFilter<T>(items: T[], options: CatalogFilterOptions<T>): CatalogFilterState<T> {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  // Note: Memoization is only effective if the caller memoizes `options`
  // (e.g., with `useMemo` or `useCallback`). With an inline `options` literal
  // (the common case for small master-data catalogs), this recomputes every
  // render, which is acceptable given current dataset sizes.
  const filtered = useMemo(() => {
    let rows = items;

    if (options.statusOf) {
      const statusOf = options.statusOf;
      if (statusFilter === "active") rows = rows.filter((r) => statusOf(r));
      else if (statusFilter === "inactive") rows = rows.filter((r) => !statusOf(r));
    }

    if (query.trim() !== "") {
      const [labelOf, ...descriptionOfs] = options.searchFields;
      const rankable: Array<RankableSearchItem & { __row: T }> = rows.map((row) => ({
        label: labelOf?.(row) ?? "",
        description: descriptionOfs.map((f) => f(row) ?? "").join(" "),
        businessCode: options.codeOf?.(row) ?? null,
        __row: row,
      }));
      rows = rankSearchMatches(rankable, query).map((r) => r.__row);
    }

    return rows;
  }, [items, query, statusFilter, options]);

  return {
    query,
    setQuery,
    statusFilter,
    setStatusFilter,
    isFiltered: query.trim() !== "" || (Boolean(options.statusOf) && statusFilter !== "all"),
    clear: () => {
      setQuery("");
      setStatusFilter("all");
    },
    filtered,
  };
}
